import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { FileCsv, Download, Buildings, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useChartColors } from "@/lib/useChartColors";

const complianceColor = (v) => v >= 95 ? "text-emerald-700 dark:text-emerald-400" : v >= 85 ? "text-amber-700 dark:text-amber-400" : "text-red-700 dark:text-red-400";

export default function ClientReport({ embedded = false }) {
  const chart = useChartColors();
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (r = range) => {
    setLoading(true);
    const params = {};
    if (r.from) params.date_from = r.from;
    if (r.to) params.date_to = r.to;
    api.get("/reports/client-summary", { params })
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load client report"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    try {
      const params = {};
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      const res = await api.get("/reports/client-summary/csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `client-summary-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const t = data?.totals;
  const truncate = (s) => (s && s.length > 18 ? s.slice(0, 16) + "…" : s);
  const chartData = (data?.rows || []).slice(0, 8).map(r => ({ name: truncate(r.client), full: r.client, hours: r.billable_hours, sla: r.sla_compliance }));

  return (
    <div className={embedded ? "" : "p-6"} data-testid="client-report-view">
      <div className="flex items-end justify-between mb-6 print:hidden">
        <div>
          {embedded ? (
            <p className="text-sm text-muted-foreground max-w-2xl">SLA compliance and billable timesheet hours rolled up per client, ready to share as a service report.</p>
          ) : (
            <>
              <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Client Service Report</div>
              <h1 className="font-display text-3xl sm:text-4xl font-black">Client Summary</h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-2xl">SLA compliance and billable timesheet hours rolled up per client, ready to share as a service report.</p>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid="cr-export-csv" onClick={exportCsv} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest"><FileCsv size={14} className="mr-2" />Export CSV</Button>
          <Button data-testid="cr-print" onClick={() => window.print()} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest"><Download size={14} className="mr-2" />Print</Button>
        </div>
      </div>

      <Card className="p-4 border-border rounded-sm mb-4 print:hidden" data-testid="cr-filters">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">From</Label><DatePicker testid="cr-date-from" value={range.from} onChange={(v) => setRange({ ...range, from: v })} placeholder="Start date" /></div>
          <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">To</Label><DatePicker testid="cr-date-to" value={range.to} onChange={(v) => setRange({ ...range, to: v })} placeholder="End date" /></div>
          <div className="flex gap-2">
            <Button data-testid="cr-run" onClick={() => load()} disabled={loading} className="rounded-sm font-mono uppercase text-xs h-10 flex-1">{loading ? "Loading…" : "Apply"}</Button>
            <Button data-testid="cr-reset" onClick={() => { const r = { from: "", to: "" }; setRange(r); load(r); }} variant="outline" className="rounded-sm font-mono uppercase text-xs h-10">Reset</Button>
          </div>
        </div>
      </Card>

      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <Card className="p-6 border-border rounded-sm" data-testid="cr-kpi-clients"><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Clients</div><div className="font-display text-4xl text-primary">{t.clients}</div></Card>
          <Card className="p-6 border-border rounded-sm" data-testid="cr-kpi-tickets"><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Tickets</div><div className="font-display text-4xl">{t.tickets}</div></Card>
          <Card className="p-6 border-border rounded-sm" data-testid="cr-kpi-sla"><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">SLA Compliance</div><div className={`font-display text-4xl ${complianceColor(t.sla_compliance)}`}>{t.sla_compliance}%</div></Card>
          <Card className="p-6 border-border rounded-sm" data-testid="cr-kpi-hours"><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Billable Hours</div><div className="font-display text-4xl text-sky-700 dark:text-sky-400">{t.billable_hours}</div></Card>
        </div>
      )}

      {chartData.length > 0 && (
        <Card className="p-6 border-border rounded-sm mb-4" data-testid="cr-chart">
          <div className="flex items-center gap-2 mb-3"><ChartBar size={16} className="text-primary" /><span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Billable hours by client <span className="text-muted-foreground/60">(top 8)</span></span></div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="name" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
              <YAxis stroke={chart.axis} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={chart.tooltip} labelFormatter={(_, p) => (p && p[0] && p[0].payload.full) || ""} />
              <Bar dataKey="hours" fill="hsl(199 89% 60%)" name="Billable hrs" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card className="border-border rounded-sm overflow-hidden" data-testid="cr-table">
        <div className="p-4 border-b border-border flex items-center gap-2"><Buildings size={16} className="text-primary" /><div className="font-display text-lg">Per-Client Breakdown</div></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
              {["Client", "SLA Plan", "Tickets", "Open", "Resolved", "Breached", "SLA %", "Avg Resp (h)", "Avg Res (h)", "Billable (h)", "Approved (h)"].map(c => <th key={c} className="text-left px-4 py-3 whitespace-nowrap">{c}</th>)}
            </tr></thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr key={r.client + i} className="border-b border-border/60 hover:bg-muted/20" data-testid={`cr-row-${i}`}>
                  <td className="px-4 py-3 text-foreground font-medium whitespace-nowrap">{r.client}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className={`text-xs font-mono px-1.5 py-0.5 border rounded-sm ${r.sla_plan && r.sla_plan !== 'Default' ? 'border-primary/50 text-primary' : 'border-border text-muted-foreground'}`}>{r.sla_plan || 'Default'}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{r.tickets}</td>
                  <td className="px-4 py-3 text-amber-700 dark:text-amber-400">{r.open}</td>
                  <td className="px-4 py-3 text-emerald-700 dark:text-emerald-400">{r.resolved}</td>
                  <td className={`px-4 py-3 ${r.breached ? "text-red-700 dark:text-red-400" : "text-muted-foreground"}`}>{r.breached}</td>
                  <td className={`px-4 py-3 font-mono ${complianceColor(r.sla_compliance)}`}>{r.sla_compliance}%</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.avg_response_hrs}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.avg_resolution_hrs}</td>
                  <td className="px-4 py-3 text-sky-700 dark:text-sky-400">{r.billable_hours}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.approved_hours}</td>
                </tr>
              ))}
              {(!data?.rows || data.rows.length === 0) && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-sm text-muted-foreground">No client data for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
